import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { fetchUserMetadata } from '@/lib/auth';
import type { UsersMetadataRow } from '@/lib/database.types';

interface UserState {
  user: User | null;
  session: Session | null;
  metadata: UsersMetadataRow | null;
  loading: boolean;
}

const INITIAL: UserState = { user: null, session: null, metadata: null, loading: true };

const UserContext = createContext<UserState>(INITIAL);

export function UserProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<UserState>(INITIAL);

  useEffect(() => {
    let mounted = true;

    const apply = (session: Session | null, metadata: UsersMetadataRow | null): void => {
      if (!mounted) return;
      setState({ user: session?.user ?? null, session, metadata, loading: false });
    };

    void supabase.auth.getSession().then(async ({ data }) => {
      const session = data.session;
      const metadata = session?.user ? await fetchUserMetadata(session.user.id) : null;
      apply(session, metadata);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      // Defer DB calls outside the auth callback to avoid the documented Supabase deadlock
      // (cf. https://supabase.com/docs/reference/javascript/auth-onauthstatechange).
      setTimeout(() => {
        void (async () => {
          const metadata = session?.user ? await fetchUserMetadata(session.user.id) : null;
          apply(session, metadata);
        })();
      }, 0);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return <UserContext.Provider value={state}>{children}</UserContext.Provider>;
}

export function useUser(): UserState {
  return useContext(UserContext);
}
